import React from 'react';
import { Tour, TourTypeLabels } from '../../types';
import { Eye, Edit, Trash2, MapPin } from 'lucide-react';
import Button from './Button';
import { extractTourText } from '../../utils/tourUtils';

interface TourCardProps {
  tour: Tour;
  onView: (tour: Tour) => void;
  onEdit: (tour: Tour) => void;
  onDelete: (tour: Tour) => void;
  onManagePoints: (tour: Tour) => void;
}

const TourCard: React.FC<TourCardProps> = ({
  tour,
  onView,
  onEdit,
  onDelete,
  onManagePoints
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="aspect-video bg-gray-100 relative">
        {tour.imageUrl ? (
          <img
            src={tour.imageUrl}
            alt={extractTourText(tour.name)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <span>No Image</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            tour.status === 'active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {tour.status || 'Unknown'}
          </span>
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {extractTourText(tour.name)}
        </h3>
        
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {extractTourText(tour.description)}
        </p>
        
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <span>Duration: {tour.duration}</span>
          <span>Rating: {tour.rating}/5</span>
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <span>Type: {TourTypeLabels[tour.type] || 'Unknown'}</span>
          <span>Status: {tour.status}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <span>Price: ${tour.sellingPrice}</span>
          <span>Place: {tour.place || 'N/A'}</span>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <span>Preview Stops: {tour.step}</span>
          <span>Distance: {tour.distance}</span>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
        <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(tour)}
            className="w-full"
          >
            <Edit className="h-4 w-4 mr-1" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManagePoints(tour)}
            className="w-full"
            title="Manage Tour Points"
          >
            <MapPin className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(tour)}
            className="w-full text-red-600 hover:text-red-700"
            title="Delete Tour"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TourCard; 